/**
 * Mobile navigation drawer, plus the sticky header's reveal behaviour.
 *
 * Below 1200px the nav becomes a full-height drawer sliding in from the right,
 * with its own markup — see the comment on .nav-drawer in header.liquid.
 *
 * Levels are full-screen panels rather than accordions. Each parent is a
 * <button>, so there is no href to suppress and no ambiguity about whether a
 * tap navigates or descends. A stack records the way in, which is what makes
 * back work at any depth without the panels knowing about each other.
 *
 * Ported from the inline IIFE in ../foamico-main/index.html.
 */
const MOBILE = '(max-width: 1200px)';

/** How far past the header you must scroll before it starts hiding. */
const HIDE_AFTER = 200;

/** Ignore jitter — only a deliberate move flips the header's state. */
const SCROLL_THRESHOLD = 6;

// Panel stylesheets (drawer, mega menu) are requested with media="print",
// which never blocks the first paint. Deferred, this runs once the page is
// parsed: switch each to the media it is really for. Their hidden states are
// in the inlined header CSS, so nothing shows before they arrive.
//
// Also run from connectedCallback: the theme editor re-renders the header
// section when it is edited, which inserts fresh `media="print"` links that a
// one-off pass at load would never reach.
const applyDeferredCss = () => {
  for (const link of document.querySelectorAll('link[data-deferred-css]')) {
    link.media = link.dataset.deferredCss || 'all';
  }
};
applyDeferredCss();

class SiteHeader extends HTMLElement {
  connectedCallback() {
    applyDeferredCss();
    this.toggle = this.querySelector('[data-nav-toggle]');
    this.nav = this.querySelector('[data-header-nav]');
    if (!this.toggle || !this.nav) return;

    this.mobile = window.matchMedia(MOBILE);

    // The overlays are siblings of this element, not children — see the note
    // in header.liquid. The section wrapper is the nearest thing that holds
    // all three.
    this.root = this.closest('.header-section') || document;
    this.drawer = this.root.querySelector('[data-nav-drawer]');
    this.panels = this.drawer ? [...this.drawer.querySelectorAll('[data-panel]')] : [];
    this.trail = [];

    this.searchToggle = this.querySelector('[data-search-toggle]');
    this.searchBar = this.root.querySelector('[data-header-search]');

    this.onToggle = this.onToggle.bind(this);
    this.onSearchToggle = this.onSearchToggle.bind(this);
    this.onNavClick = this.onNavClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onDocClick = this.onDocClick.bind(this);
    this.onBreakpoint = this.onBreakpoint.bind(this);

    this.toggle.addEventListener('click', this.onToggle);
    this.searchToggle?.addEventListener('click', this.onSearchToggle);
    this.drawer?.addEventListener('click', this.onNavClick);
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocClick);
    this.mobile.addEventListener('change', this.onBreakpoint);

    if (this.hasAttribute('data-sticky')) {
      // 'always' stays put, so there is nothing to watch the scroll for: the
      // header is never hidden and its published height never changes.
      if (this.getAttribute('data-sticky-mode') === 'always') {
        this.publishHeight(false);
      } else {
        this.lastScroll = window.scrollY;
        this.onScroll = this.onScroll.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.publishHeight(false);
      }
    }
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.onToggle);
    this.searchToggle?.removeEventListener('click', this.onSearchToggle);
    this.drawer?.removeEventListener('click', this.onNavClick);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocClick);
    this.mobile?.removeEventListener('change', this.onBreakpoint);
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    cancelAnimationFrame(this.frame);
  }

  get isOpen() {
    return Boolean(this.drawer?.classList.contains('is-open'));
  }

  get isSearchOpen() {
    return Boolean(this.searchBar?.classList.contains('is-open'));
  }

  openSearch() {
    if (!this.searchBar) return;
    // Two full-height sheets over one small screen is nobody's idea of a
    // menu. Whichever was open loses.
    if (this.isOpen) this.closeMenu();

    this.searchBar.classList.add('is-open');
    this.searchToggle.classList.add('is-active');
    this.searchToggle.setAttribute('aria-expanded', 'true');
    // The sheet covers the viewport; the page behind it must not scroll.
    document.body.classList.add('is-search-open');

    this.focusSearch();
  }

  /**
   * Puts the caret in the search field.
   *
   * The forced reflow is load-bearing: the sheet is `visibility: hidden` until
   * `is-open` takes effect, and a hidden element cannot take focus. At the
   * moment the class is added the style has not been recalculated, so focus()
   * silently does nothing. Reading a layout property makes the browser catch
   * up first.
   *
   * The first attempt stays inside the click that triggered it, which is what
   * mobile browsers require before they will raise the keyboard — a focus()
   * deferred to a later task moves the caret but leaves the keyboard down. The
   * timed retry is only for the case where the reflow was not enough; by then
   * the sheet has certainly been painted, but the keyboard may not follow.
   */
  focusSearch() {
    const input = this.searchBar?.querySelector('input[type="search"]');
    if (!input) return;

    // preventScroll: the sheet is fixed, so there is nothing to scroll to —
    // without it the page behind can jump before the overlay covers it.
    void this.searchBar.offsetHeight;
    input.focus({ preventScroll: true });

    if (document.activeElement === input) return;
    // requestAnimationFrame(() => {
    //   const isVisible = input.offsetWidth > 0 || input.offsetHeight > 0;
    //   if (this.isSearchOpen && isVisible) {
    //     input.focus({ preventScroll: true });
    //   }
    // });
    // requestAnimationFrame(() => {
    //   if (this.isSearchOpen) input.focus({ preventScroll: true });
    // });
      // Macrotask fallback bypasses transition frames
    // setTimeout(() => {
    //   if (this.isSearchOpen) {
    //     input.focus({ preventScroll: true });
    //   }
    // }, 50);
    const handleTransitionEnd = (e) => {
    // Only trigger if the main sheet finished animating
      if (e.target === this.searchBar && this.isSearchOpen) {
        input.focus({ preventScroll: true });
        this.searchBar.removeEventListener('transitionend', handleTransitionEnd);
      }
    };

    this.searchBar.addEventListener('transitionend', handleTransitionEnd);
  }

  closeSearch() {
    if (!this.searchBar) return;
    this.searchBar.classList.remove('is-open');
    this.searchToggle.classList.remove('is-active');
    this.searchToggle.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('is-search-open');
  }

  onSearchToggle() {
    if (this.isSearchOpen) this.closeSearch();
    else this.openSearch();
  }

  openMenu() {
    if (this.isSearchOpen) this.closeSearch();
    this.drawer?.classList.add('is-open');
    this.toggle.classList.add('is-active');
    this.toggle.setAttribute('aria-expanded', 'true');
    // The drawer covers the viewport; the page behind it must not scroll.
    document.body.classList.add('is-nav-open');
  }

  closeMenu() {
    this.drawer?.classList.remove('is-open');
    this.toggle.classList.remove('is-active');
    this.toggle.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('is-nav-open');
    // Reset to the first level, so reopening never resumes mid-drill.
    this.showPanel('root');
    this.trail = [];
  }

  showPanel(key) {
    for (const panel of this.panels) {
      panel.classList.toggle('is-active', panel.dataset.panel === key);
    }
  }

  onToggle() {
    if (this.isOpen) this.closeMenu();
    else this.openMenu();
  }

  onNavClick(event) {
    const forward = event.target.closest('[data-open-panel]');
    if (forward) {
      const current = this.panels.find((p) => p.classList.contains('is-active'));
      this.trail.push(current?.dataset.panel || 'root');
      this.showPanel(forward.dataset.openPanel);
      return;
    }

    const back = event.target.closest('[data-close-panel]');
    if (back) {
      this.trail.pop();
      this.showPanel(back.dataset.closePanel);
      return;
    }

    // Anything else is a real link, so let it navigate and shut the drawer.
    if (event.target.closest('a')) this.closeMenu();
  }

  /** Escape steps back a level first, and only then shuts the drawer. */
  onKeydown(event) {
    if (event.key !== 'Escape') return;

    if (this.isSearchOpen) {
      this.closeSearch();
      return;
    }
    if (!this.isOpen) return;

    if (this.trail.length) this.showPanel(this.trail.pop());
    else this.closeMenu();
  }

  onDocClick(event) {
    const path = event.composedPath();

    if (this.isSearchOpen && !path.includes(this.searchBar) && !path.includes(this.searchToggle)) {
      this.closeSearch();
    }

    if (!this.isOpen) return;
    if (!path.includes(this.drawer) && !path.includes(this.toggle)) this.closeMenu();
  }

  /** Back on a wide screen both of these are mobile-only chrome. */
  onBreakpoint(event) {
    if (event.matches) return;
    this.closeMenu();
    this.closeSearch();
  }

  /** Scroll fires far more often than the screen repaints, so coalesce. */
  onScroll() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.updateSticky());
  }

  updateSticky() {
    const y = Math.max(0, window.scrollY);
    const delta = y - this.lastScroll;

    // An open sheet must never be scrolled out from under the visitor — and
    // hiding the header applies a transform to it, which would make it the
    // containing block for the sheet's `position: fixed` and collapse it.
    if (this.isOpen || this.isSearchOpen) {
      this.classList.remove('is-hidden');
      this.lastScroll = y;
      return;
    }

    if (Math.abs(delta) < SCROLL_THRESHOLD) return;

    // Near the top the header always shows, so the page never opens headless.
    if (y < HIDE_AFTER) this.classList.remove('is-hidden');
    else this.classList.toggle('is-hidden', delta > 0);

    this.publishHeight(this.classList.contains('is-hidden'));
    this.lastScroll = y;
  }

  /**
   * Publishes how much header is actually on screen.
   *
   * Anything else that sticks below the header — the product tab bar — needs
   * to follow it up and down, and only the header knows when it is hidden.
   */
  publishHeight(hidden) {
    const height = hidden ? '0px' : `${Math.round(this.getBoundingClientRect().height)}px`;
    document.documentElement.style.setProperty('--header-visible-height', height);
  }
}

customElements.define('site-header', SiteHeader);
